import { useHasAllocations } from '@/allocation/composables/useHasAllocations';
import auth from '@/auth/auth';
import {
    CreateMultiLinkDocument,
    DeleteEventDocument,
    EventVariantsWithAllocationsCountDocument,
    GetEventDocument,
    UpdateEventDocument,
    UpdateEventMultiLinkDocument,
    UpdateEventMutationVariables,
    UpdateEventStatusDocument,
} from '@/generated/graphql';
import { thumbnailUrl } from '@/utils/file';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { useFluent } from 'fluent-vue';
import { computed, Ref, toValue } from 'vue';
import { useRouter } from 'vue-router';
import { EventStatus } from '../types';

export function useEvent(eventId: Ref<string>) {
    const router = useRouter();
    const fluent = useFluent();
    const isAdmin = auth.isAdmin();

    const { hasAllocations, refetchHasAllocations } = useHasAllocations(
        EventVariantsWithAllocationsCountDocument,
        eventId,
    );

    const { result } = useQuery(
        GetEventDocument,
        () => ({ id: toValue(eventId) }),
        () => ({
            enabled: !!toValue(eventId),
        }),
    );

    const event = computed(() => result.value?.event);

    const iconUrl = computed(() => thumbnailUrl(event.value?.icon ?? event.value?.series?.icon, 'url512'));

    const updateStatusMutation = useMutation(UpdateEventStatusDocument);
    async function updateEventStatus(status: EventStatus) {
        if (!event.value) return;
        if (status === event.value.status) return;
        const seriesId = event.value.id;
        const response = await updateStatusMutation.mutate({ id: seriesId, status });
        if (response?.errors) throw new Error(`Could not update event status: ${response.errors}`);
    }

    const deleteEventMutation = useMutation(DeleteEventDocument);
    const deleteEvent = async () => {
        if (!event.value) return;
        if (!isAdmin || hasAllocations.value) return;

        if (!confirm(fluent.$t('confirm-delete-event', { event: event.value.nameShort }))) return;

        const response = await deleteEventMutation.mutate({ eventId: toValue(eventId) });
        if (response?.errors) {
            throw Error(`Could not delete event: ${response.errors}`);
        }
        if (response?.data) {
            router.replace({ name: 'events' });
        }
    };

    const updateEventMutation = useMutation(UpdateEventDocument);
    async function updateEvent(data: UpdateEventMutationVariables) {
        const result = await updateEventMutation.mutate(data);
        if (result?.errors) {
            throw Error(`Error on update event series: ${result.errors}`);
        }
    }

    const updateEventMultiLinksMutation = useMutation(UpdateEventMultiLinkDocument);
    async function updateEventMultiLinkId(fieldName: string, multiLinkId: string) {
        if (!event.value) return;
        const response = await updateEventMultiLinksMutation.mutate({ id: event.value.id, [fieldName]: multiLinkId });
        if (response?.errors) {
            throw new Error(`Could not update event multilink ${fieldName}: ${response.errors}`);
        }
    }

    const createMultiLinkMutation = useMutation(CreateMultiLinkDocument);
    async function createEventMultiLink(fieldName: string) {
        if (!event.value) return;
        const name = `${event.value.id}-${fieldName}`;
        const response = await createMultiLinkMutation.mutate({ name });
        if (response?.errors) {
            throw new Error(`Could not create Multilink for event series: ${response.errors}`);
        }
        if (response?.data) {
            const multiLinkId = response.data.createMultilink?.multilink?.id;
            if (multiLinkId) {
                await updateEventMultiLinkId(`${fieldName}Id`, multiLinkId);
                return multiLinkId;
            }
        }
    }

    return {
        event,
        iconUrl,
        updateEventStatusError: updateStatusMutation.error,
        updateEventStatusLoading: updateStatusMutation.loading,
        updateEventStatus,
        updateEvent,
        deleteEvent,
        createEventMultiLink,
        hasAllocations,
        refetchHasAllocations,
    };
}
